import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Link as LinkIcon } from "lucide-react";

const BLANK = {
  title: "", url: "", description: "", kind: "other",
  course_id: "", subject_id: "", class_id: "",
  audience: "course_enrolled", student_ids: [],
  available_from: "", available_until: "", is_active: true,
};

export default function ResourcesTab() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = () => Promise.all([
    api.get("/admin/resources"),
    api.get("/admin/users"),
    api.get("/admin/courses"),
    api.get("/subjects"),
  ]).then(([r, u, c, s]) => { setItems(r.data); setUsers(u.data); setCourses(c.data); setSubjects(s.data); });
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...BLANK, ...r,
      course_id: r.course_id || "", subject_id: r.subject_id || "", class_id: r.class_id || "",
      available_from: r.available_from || "", available_until: r.available_until || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      // validate URL
      if (!/^https?:\/\//i.test(form.url)) {
        toast.error("URL must start with http:// or https://");
        return;
      }
      const payload = {
        ...form,
        course_id: form.course_id || null,
        subject_id: form.subject_id || null,
        class_id: form.class_id || null,
        available_from: form.available_from || null,
        available_until: form.available_until || null,
      };
      if (editing) await api.put(`/admin/resources/${editing.id}`, payload);
      else await api.post("/admin/resources", payload);
      toast.success(editing ? "Updated" : "Resource added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const del = async (r) => {
    if (!confirm(`Delete "${r.title}"?`)) return;
    await api.delete(`/admin/resources/${r.id}`);
    toast.success("Deleted"); load();
  };
  const toggleStudent = (uid) => {
    const set = new Set(form.student_ids);
    if (set.has(uid)) set.delete(uid); else set.add(uid);
    setForm({ ...form, student_ids: Array.from(set) });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="new-resource-btn">
          <Plus className="mr-2 h-4 w-4" /> Add link / resource
        </Button>
      </div>
      <Card className="border-border/70">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Audience</TableHead>
              <TableHead>Course / Subject</TableHead>
              <TableHead>Active</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="font-medium">{r.title}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-xs">{r.url}</div>
                  </TableCell>
                  <TableCell className="text-xs capitalize">{r.kind}</TableCell>
                  <TableCell className="text-xs capitalize">{r.audience.replace("_", " ")}</TableCell>
                  <TableCell className="text-xs">
                    {courses.find((c) => c.id === r.course_id)?.name || subjects.find((s) => s.id === r.subject_id)?.name || "—"}
                  </TableCell>
                  <TableCell className="text-xs">{r.is_active ? "Yes" : "No"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-res-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(r)} data-testid={`del-res-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="p-10 text-center text-sm text-muted-foreground">No resources yet. Add a Zoom / Meet / Drive link.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit resource" : "Add class link / resource"}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="res-title" /></div>
              <div>
                <Label>Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="res-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zoom">Zoom meeting</SelectItem>
                    <SelectItem value="meet">Google Meet</SelectItem>
                    <SelectItem value="teams">MS Teams</SelectItem>
                    <SelectItem value="stream">Live stream</SelectItem>
                    <SelectItem value="recording">Recording</SelectItem>
                    <SelectItem value="drive">Drive / Document</SelectItem>
                    <SelectItem value="material">Material / PDF</SelectItem>
                    <SelectItem value="other">Other link</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="res-url" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="res-desc" /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Linked course</Label>
                <Select value={form.course_id || "none"} onValueChange={(v) => setForm({ ...form, course_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="res-course"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Linked subject</Label>
                <Select value={form.subject_id || "none"} onValueChange={(v) => setForm({ ...form, subject_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="res-subject"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Who can see this?</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger data-testid="res-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_students">All logged-in students</SelectItem>
                  <SelectItem value="course_enrolled">Only students enrolled in the linked course</SelectItem>
                  <SelectItem value="specific_students">Only specific students (choose below)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.audience === "specific_students" && (
              <div>
                <Label>Pick students ({form.student_ids.length} selected)</Label>
                <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-border p-2">
                  {users.filter((u) => u.role === "student").map((u) => (
                    <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-secondary">
                      <input type="checkbox" checked={form.student_ids.includes(u.id)} onChange={() => toggleStudent(u.id)} data-testid={`res-student-${u.id}`} />
                      <span>{u.name} · {u.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Available from</Label><Input type="datetime-local" value={form.available_from} onChange={(e) => setForm({ ...form, available_from: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="res-from" /></div>
              <div><Label>Available until</Label><Input type="datetime-local" value={form.available_until} onChange={(e) => setForm({ ...form, available_until: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="res-until" /></div>
            </div>
            <div>
              <Label>Active</Label>
              <Select value={form.is_active ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, is_active: v === "yes" })}>
                <SelectTrigger data-testid="res-active"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Active (visible to audience)</SelectItem>
                  <SelectItem value="no">Inactive (hidden)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="res-save-btn">
              <LinkIcon className="mr-2 h-4 w-4" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
